import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Lock, CheckCircle, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTheme } from "@/contexts/ThemeContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import MuslinkLogo from "@/components/MuslinkLogo";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { theme } = useTheme();
  const token = searchParams.get("token");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setError(t('auth', 'invalidResetLink'));
    }
  }, [token, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error(t('auth', 'passwordMinLength'));
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error(t('auth', 'passwordMismatch'));
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success(t('auth', 'passwordResetSuccess'));
    } catch (err) {
      const message = err.response?.data?.detail || t('errors', 'generic');
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      <div className="absolute top-4 right-4 z-10">
        <LanguageSwitcher variant="compact" />
      </div>

      {/* Left Side - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-8 py-8 sm:py-12 lg:px-16">
        <div className="w-full max-w-md mx-auto">
          <Link to="/login" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 sm:mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm sm:text-base">{t('auth', 'backToLogin')}</span>
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-2 mb-6 sm:mb-8">
              <Link to="/">
                <MuslinkLogo height={32} theme={theme} />
              </Link>
            </div>
            
            {error && !success ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
                  <AlertCircle className="w-8 h-8 text-red-500" />
                </div>
                <h1 className="text-2xl sm:text-3xl font-semibold mb-4">{t('common', 'error')}</h1>
                <p className="text-muted-foreground mb-8">{error}</p>
                <Link to="/forgot-password">
                  <Button className="bg-primary hover:bg-primary/90 rounded-xl" data-testid="request-new-link-btn">
                    {t('auth', 'requestNewLink')}
                  </Button>
                </Link>
              </motion.div>
            ) : success ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <h1 className="text-2xl sm:text-3xl font-semibold mb-4">{t('auth', 'passwordChangedTitle')}</h1>
                <p className="text-muted-foreground mb-8">
                  {t('auth', 'passwordChangedDesc')}
                </p>
                <Button 
                  onClick={() => navigate("/login")}
                  className="bg-primary hover:bg-primary/90 rounded-xl"
                  data-testid="go-to-login-btn"
                >
                  {t('auth', 'goToLogin')}
                </Button>
              </motion.div>
            ) : (
              <>
                <h1 className="text-2xl sm:text-3xl font-semibold mb-2">{t('auth', 'resetPasswordTitle')}</h1>
                <p className="text-muted-foreground mb-8">
                  {t('auth', 'resetPasswordSubtitle')}
                </p>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="password">{t('auth', 'newPassword')}</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="reset-password-input"
                      className="h-12 bg-muted border-border focus:border-primary"
                    />
                    <p className="text-xs text-muted-foreground">{t('auth', 'minPasswordHint')}</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">{t('auth', 'confirmNewPassword')}</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      data-testid="reset-confirm-password-input"
                      className="h-12 bg-muted border-border focus:border-primary"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    disabled={loading}
                    data-testid="reset-submit-btn"
                    className="w-full h-12 bg-primary hover:bg-primary/90 rounded-xl font-semibold"
                  >
                    {loading ? t('auth', 'saving') : t('auth', 'resetButton')}
                  </Button>
                </form>
              </>
            )}
          </motion.div>
        </div>
      </div>
      
      {/* Right Side - Visual */}
      <div className={`hidden lg:flex flex-1 ${
        theme === 'dark'
          ? 'bg-gradient-to-br from-primary/20 via-zinc-900 to-background'
          : 'bg-gradient-to-br from-primary/10 via-gray-100 to-white'
      } items-center justify-center p-12`}>
        <div className="w-full max-w-2xl mx-auto flex flex-col items-center justify-center text-center">
          <div className="w-32 h-32 xl:w-40 xl:h-40 flex items-center justify-center mb-6">
            <Lock className="w-16 h-16 text-primary" />
          </div>
          <h2 className={`font-display text-2xl xl:text-3xl mb-4 whitespace-nowrap ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            {t('auth', 'securityTitle')}
          </h2>
          <p className={`whitespace-nowrap ${theme === 'dark' ? 'text-zinc-400' : 'text-gray-600'}`}>
            {t('auth', 'securityDesc')}
          </p>
        </div>
      </div>
    </div>
  );
}
